const dsteem = require('dsteem');
const steem = require('steem');

steem.api.setOptions({ url: 'https://api.steemit.com' });
if (steem.config) {
  steem.config.set('websocket', 'https://api.steemit.com');
  steem.config.set('address_prefix', 'STM');
  steem.config.set('chain_id', '0000000000000000000000000000000000000000000000000000000000000000');
}

const client = new dsteem.Client('https://api.steemit.com');

const DEFAULT_AUTH_ACCOUNT = process.env.REACT_APP_AUTH_ACCOUNT || 'steem-seven';

/**
 * Checks if targetAccount is authorized in the user's posting authority
 * @param {string} username The Steem username to check
 * @param {string} targetAccount Account to check for authority
 * @returns {Promise<boolean>}
 */
export async function checkIsAuthorized(username, targetAccount = DEFAULT_AUTH_ACCOUNT) {
  if (!username) return false;
  const user = username.trim().toLowerCase().replace('@', '');
  const target = targetAccount.trim().toLowerCase().replace('@', '');

  try {
    const accounts = await client.database.getAccounts([user]);
    if (accounts && accounts.length > 0) {
      const account = accounts[0];
      const threshold = account.posting.weight_threshold || 1;
      const postingAuths = account.posting.account_auths || [];

      const found = postingAuths.find(([authAcc, weight]) => authAcc.toLowerCase() === target && weight >= threshold);
      return Boolean(found);
    }
    return false;
  } catch (error) {
    try {
      const accounts = await new Promise((res, rej) => {
        steem.api.getAccounts([user], (e, r) => e ? rej(e) : res(r));
      });
      if (accounts && accounts.length > 0) {
        const account = accounts[0];
        const threshold = account.posting.weight_threshold || 1;
        const postingAuths = account.posting.account_auths || [];
        const found = postingAuths.find(([authAcc, weight]) => authAcc.toLowerCase() === target && weight >= threshold);
        return Boolean(found);
      }
      return false;
    } catch (e) {
      console.error("Error checking authority:", e);
      return false;
    }
  }
}

/**
 * Grants posting authority to targetAccount using Private Active Key (in-memory only, no cookies/storage)
 * @param {string} username User granting authority
 * @param {string} activeWif Private Active Key
 * @param {string} targetAccount Account receiving authority
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function authorizeWithActiveKey(username, activeWif, targetAccount = DEFAULT_AUTH_ACCOUNT) {
  if (!username || !activeWif) {
    return { success: false, message: "Please provide your Private Active Key." };
  }

  const user = username.trim().toLowerCase().replace('@', '');
  const target = targetAccount.trim().toLowerCase().replace('@', '');
  const keyInput = activeWif.trim();

  try {
    const accounts = await client.database.getAccounts([user]);
    if (!accounts || accounts.length === 0) {
      return { success: false, message: `Account @${user} does not exist.` };
    }

    const account = accounts[0];
    const activepub = account.active.key_auths[0][0];

    // Validate that the provided key is indeed the active key
    let isValidActive = false;
    try {
      const privKey = dsteem.PrivateKey.fromString(keyInput);
      if (privKey.createPublic('STM').toString() === activepub) {
        isValidActive = true;
      }
    } catch (e) {}

    try {
      if (!isValidActive && steem.auth.wifIsValid(keyInput, activepub)) {
        isValidActive = true;
      }
    } catch (e) {}

    // Test if user provided master password
    let activeKeyWif = keyInput;
    if (!isValidActive) {
      try {
        const derived = steem.auth.getPrivateKeys(user, keyInput, ['active']);
        if (derived && derived.activePubkey === activepub) {
          isValidActive = true;
          activeKeyWif = derived.active;
        }
      } catch (e) {}
    }

    if (!isValidActive) {
      return { success: false, message: "Invalid Private Active Key for @" + user };
    }

    // Check if already authorized
    const isAlready = account.posting.account_auths.some(([authAcc]) => authAcc.toLowerCase() === target);
    if (isAlready) {
      return { success: true, message: `Account @${target} is already authorized.` };
    }

    // Prepare new posting authority
    const newPosting = JSON.parse(JSON.stringify(account.posting));
    newPosting.account_auths.push([target, 1]);
    newPosting.account_auths.sort((a, b) => a[0].localeCompare(b[0]));

    const operation = [
      'account_update',
      {
        account: user,
        posting: newPosting,
        memo_key: account.memo_key,
        json_metadata: account.json_metadata || ''
      }
    ];

    const privKey = dsteem.PrivateKey.fromString(activeKeyWif);
    const result = await client.broadcast.sendOperations([operation], privKey);

    return {
      success: true,
      message: `Posting authority successfully granted to @${target}!`,
      txId: result.id
    };
  } catch (error) {
    console.error("Failed to authorize with Active Key:", error);
    return {
      success: false,
      message: error?.message || "Failed to broadcast authority update to Steem."
    };
  }
}

/**
 * Grants posting authority to targetAccount using Steem Keychain
 * @param {string} username User granting authority
 * @param {string} targetAccount Account receiving authority
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function authorizeWithKeychain(username, targetAccount = DEFAULT_AUTH_ACCOUNT) {
  return new Promise((resolve) => {
    if (!window.steem_keychain) {
      return resolve({ success: false, message: "Steem Keychain extension not found." });
    }

    const user = username.trim().toLowerCase().replace('@', '');
    const target = targetAccount.trim().toLowerCase().replace('@', '');

    window.steem_keychain.requestAddAccountAuthority(
      user,
      target,
      "Posting",
      1,
      (response) => {
        if (response && response.success) {
          resolve({ success: true, message: `Posting authority granted to @${target} via Keychain!` });
        } else {
          resolve({
            success: false,
            message: response?.message || "Keychain authorization was cancelled or rejected."
          });
        }
      }
    );
  });
}
